import { normalizeSalesImportBranchId } from "../features/salesDataImport/config/salesImportBranchIds";
import {
  listUploadedWeekPeriods,
  loadLineChunkRowsForDateRange,
  loadWeeklyLineChunkRows,
  normalizeVoucherType,
  lineBillAmountHeaderOnly,
  lineAmountOnly,
  resolveImportVoucherTotal,
} from "../features/salesDataImport/services/salesLineChunksReader";
import {
  isExactVoucherTypeRow,
  isOutwardSupplyRow,
  isInwardSupplyRow,
} from "./billingImportFindService";

export const MAIN_BRANCH_SALE_VOUCHER_TYPES = ["Outward Supply", "Credit Sales"];

/** Credit Note voucher types → sales return in monthly report. */
export function isCreditNoteVoucherType(vt) {
  const s = String(vt || "").toLowerCase().replace(/\s+/g, " ");
  if (!s.includes("credit") || !s.includes("note")) return false;
  return true;
}

/** Branch suffix in voucher type, e.g. Credit Note (BRANCH 1), Credit Note Br Branch 10. */
export function appBranchFromVoucherType(vt) {
  const s = String(vt || "").toUpperCase().replace(/\s+/g, " ");
  if (
    s.includes("CREDIT SALES WS") ||
    s.includes("OUTWARD SUPPLY WS") ||
    s.includes("WHOLESALE") ||
    (/\bWS\b/.test(s) && (s.includes("CREDIT") || s.includes("OUTWARD")))
  ) {
    return "Wholesale";
  }
  if (s.includes("BRANCH 11") || s.includes("SPAREWAY") || s.includes("SPARE WAY")) return "Branch 2";
  if (s.includes("BRANCH 10") || s.includes("BRANCH 3")) return "Branch 3";
  if (s.includes("BRANCH 1")) return "Main";
  return null;
}

/** BranchID from SQL import → app branch filter (Monthly Sale Report). */
export function appBranchFromImportRow(row) {
  const vt = normalizeVoucherType(row);
  if (isCreditSalesWsRow(row) || isOutwardSupplyWsRow(row)) return "Wholesale";
  if (isCreditNoteVoucherType(vt)) {
    const fromVt = appBranchFromVoucherType(vt);
    if (fromVt) return fromVt;
  }
  const fromSaleVt = appBranchFromVoucherType(vt);
  if (fromSaleVt === "Wholesale") return "Wholesale";
  const id = normalizeSalesImportBranchId(row?.branchId ?? row?.BranchID ?? row?.branch ?? "");
  if (id === "11") return "Branch 2";
  if (id === "10") return "Branch 3";
  if (id === "1") return "Main";
  return "Main";
}

function isCreditSalesVoucherRow(row) {
  return (
    isExactVoucherTypeRow(row, "Credit Sales") ||
    isExactVoucherTypeRow(row, "Credit Sales Spareway") ||
    isExactVoucherTypeRow(row, "Credit Sales Br") ||
    isCreditSalesWsRow(row)
  );
}

function cashCreditFromVoucherType(vt) {
  if (isCreditNoteVoucherType(vt)) return "CASH";
  if (isCreditSalesVoucherRow({ voucherType: vt })) return "CREDIT";
  return "CASH";
}

/** Exact "Credit Sales" only — excludes Credit Sales Br / Spareway variants. */
export function isCreditSalesRow(row) {
  return isExactVoucherTypeRow(row, "Credit Sales");
}

/** Spareway (Branch ID 11) — exact SQL voucher labels. */
export function isOutwardSupplySparewayRow(row) {
  return isExactVoucherTypeRow(row, "Outward Supply Spareway");
}

export function isCreditSalesSparewayRow(row) {
  return isExactVoucherTypeRow(row, "Credit Sales Spareway");
}

/** Branch 3 (Branch ID 10) — exact SQL voucher labels. */
export function isOutwardSupplyBrRow(row) {
  return isExactVoucherTypeRow(row, "Outward Supply Br");
}

export function isCreditSalesBrRow(row) {
  return isExactVoucherTypeRow(row, "Credit Sales Br");
}

/** Wholesale — exact SQL voucher label Credit Sales WS. */
export function isCreditSalesWsRow(row) {
  return isExactVoucherTypeRow(row, "Credit Sales WS");
}

export function isOutwardSupplyWsRow(row) {
  return isExactVoucherTypeRow(row, "Outward Supply WS");
}

/** Main Branch sales from import: Outward Supply + Credit Sales (exact names only). */
export function isMainBranchImportSaleVoucherRow(row) {
  return isOutwardSupplyRow(row) || isCreditSalesRow(row);
}

/** Spareway import sale vouchers. */
export function isSparewayImportSaleVoucherRow(row) {
  return isOutwardSupplySparewayRow(row) || isCreditSalesSparewayRow(row);
}

/** Branch 3 import sale vouchers. */
export function isBranch3ImportSaleVoucherRow(row) {
  return isOutwardSupplyBrRow(row) || isCreditSalesBrRow(row);
}

/** Wholesale import sale vouchers. */
export function isWholesaleImportSaleVoucherRow(row) {
  return isCreditSalesWsRow(row) || isOutwardSupplyWsRow(row);
}

/** Any outlet sale voucher from SQL import (Main / Spareway / Branch 3 / Wholesale). */
export function isImportSaleVoucherRow(row) {
  return (
    isMainBranchImportSaleVoucherRow(row) ||
    isSparewayImportSaleVoucherRow(row) ||
    isBranch3ImportSaleVoucherRow(row) ||
    isWholesaleImportSaleVoucherRow(row)
  );
}

/** Outward / Credit Sales on grouped import invoice docs (all branch variants). */
export function isImportSaleVoucherInvoice(inv) {
  const row = { voucherType: inv?.transactionClass ?? inv?.voucherType ?? "" };
  return isImportSaleVoucherRow(row);
}

export function importRowBranchId(row) {
  return normalizeSalesImportBranchId(row?.branchId ?? row?.BranchID ?? row?.branch ?? "");
}

/** Party name WINS BRANCH (S.DEBTOR) on Transfer Out purchase lines. */
export function isWinsBranchSDebtorParty(row) {
  const name = String(row?.customerName ?? row?.customer ?? row?.party ?? row?.partyName ?? "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();
  if (!name) return false;
  const compact = name.replace(/[\s.\-_/()]/g, "");
  return (
    name.includes("WINS") &&
    name.includes("BRANCH") &&
    (name.includes("S.DEBTOR") || name.includes("S DEBTOR") || compact.includes("SDEBTOR"))
  );
}

/** Voucher type Transfer Out - New (exact SQL / export label). */
export function isTransferOutNewVoucherRow(row) {
  if (isExactVoucherTypeRow(row, "Transfer Out - New")) return true;
  const vt = normalizeVoucherType(row).toUpperCase();
  return vt.includes("TRANSFER") && vt.includes("OUT") && vt.includes("NEW");
}

/** Voucher type Transfer IN (exact or normalized export label). */
export function isTransferInVoucherRow(row) {
  if (isExactVoucherTypeRow(row, "Transfer IN")) return true;
  const vt = normalizeVoucherType(row).toUpperCase();
  return (
    vt === "TRANSFER IN" ||
    (vt.includes("TRANSFER") &&
      /\bIN\b/.test(vt) &&
      !vt.includes("INVOICE") &&
      !vt.includes("INWARD") &&
      !vt.includes("INTERSTATE"))
  );
}

/** Branch / inter-branch transfer vouchers incl. Transfer Out - New and Transfer IN. */
export function isBranchTransferVoucherRow(row) {
  if (isTransferOutNewVoucherRow(row) || isTransferInVoucherRow(row)) return true;
  const vt = normalizeVoucherType(row).toUpperCase();
  return (
    (vt.includes("BRANCH") && vt.includes("TRANSFER")) ||
    vt.includes("INTER BRANCH") ||
    vt.includes("INTER-BRANCH") ||
    vt.includes("INTERBRANCH")
  );
}

export function isTransferOutDirectionVoucherRow(row) {
  if (isTransferOutNewVoucherRow(row)) return true;
  const tc = normalizeVoucherType(row).toUpperCase();
  return (
    tc.includes("TRANSFER OUT") ||
    tc.includes("OUTWARD") ||
    (tc.includes("OUT") && !tc.includes("WITHOUT"))
  );
}

export function isTransferInDirectionVoucherRow(row) {
  if (isTransferInVoucherRow(row)) return true;
  const tc = normalizeVoucherType(row).toUpperCase();
  return (
    tc.includes("TRANSFER INWARD") ||
    (tc.includes("TRANSFER IN") && !tc.includes("INVOICE")) ||
    (tc.includes("INWARD") && !tc.includes("OUTWARD")) ||
    (/\bIN\b/.test(tc) && !tc.includes("INVOICE") && !tc.includes("INTERSTATE"))
  );
}

/**
 * Main Branch purchase (add): Inward Supply · BranchID 1
 */
export function isMainBranchInwardSupplyPurchaseRow(row) {
  return importRowBranchId(row) === "1" && isInwardSupplyRow(row);
}

/** Main Branch purchase (deduct): Transfer Out - New · WINS BRANCH (S.DEBTOR) · BranchID 1 */
export function isMainBranchTransferOutPurchaseDeductionRow(row) {
  if (importRowBranchId(row) !== "1") return false;
  return isTransferOutNewVoucherRow(row) && isWinsBranchSDebtorParty(row);
}

export function isMainBranchImportPurchaseVoucherRow(row) {
  return isMainBranchInwardSupplyPurchaseRow(row);
}

/**
 * Main Branch monthly report row filter (also used while loading chunks for speed).
 */
export function includeRowInMonthlyImportReport(row, branchFilter) {
  if (branchFilter === "Wholesale") {
    const vt = normalizeVoucherType(row);
    if (isCreditNoteVoucherType(vt)) return appBranchFromImportRow(row) === "Wholesale";
    return isWholesaleImportSaleVoucherRow(row);
  }
  if (branchFilter !== "Main") return true;

  const id = normalizeSalesImportBranchId(row?.branchId ?? row?.BranchID ?? row?.branch ?? "");
  const vt = normalizeVoucherType(row);

  if (isCreditNoteVoucherType(vt)) {
    return appBranchFromImportRow(row) === "Main";
  }
  if (id && id !== "1") return false;

  if (isMainBranchInwardSupplyPurchaseRow(row)) return true;
  if (isMainBranchTransferOutPurchaseDeductionRow(row)) return true;
  if (isTransferOutNewVoucherRow(row)) return importRowBranchId(row) === "1";
  if (isTransferInVoucherRow(row)) return importRowBranchId(row) === "1";
  return isMainBranchImportSaleVoucherRow(row);
}

/** Previous-month columns: sales/purchase rows only (credit notes are current-month returns). */
export function includeRowInMonthlyImportReportPrevMonth(row, branchFilter) {
  if (!includeRowInMonthlyImportReport(row, branchFilter)) return false;
  return !isCreditNoteVoucherType(normalizeVoucherType(row));
}

/**
 * Main Branch monthly report uses only Outward Supply + Credit Sales voucher types.
 * Other branches keep all import rows for that branch.
 */
export function filterImportRowsForMonthlyReport(rows, branchFilter) {
  if (branchFilter === "Wholesale") {
    return (rows || []).filter((row) => includeRowInMonthlyImportReport(row, "Wholesale"));
  }
  if (branchFilter !== "Main") return rows || [];
  return (rows || []).filter((row) => includeRowInMonthlyImportReport(row, branchFilter));
}

/** @returns {"inward"|"outward"|"transfer"|"creditnote"} */
export function invoiceTypeFromImportRow(row) {
  const vt = normalizeVoucherType(row).toLowerCase();
  if (isCreditNoteVoucherType(vt)) return "creditnote";
  if (isMainBranchInwardSupplyPurchaseRow(row)) return "inward";
  // Transfer vouchers before generic inward — avoids counting transfer IN twice as purchase.
  if (isBranchTransferVoucherRow(row)) return "transfer";
  if (isInwardSupplyRow(row)) return "inward";
  if (isImportSaleVoucherRow(row)) return "outward";
  if (vt.includes("inward") && vt.includes("supply")) return "inward";
  if (vt.includes("outward") && vt.includes("supply")) return "outward";
  return "outward";
}

/** @returns {"inward"|"outward"|null} */
export function movementTypeFromImportRow(row) {
  const t = invoiceTypeFromImportRow(row);
  return t === "creditnote" ? null : t;
}

export async function loadSalesImportRows({
  fromDate = "",
  toDate = "",
  maxWeeks = 24,
  rowFilter = null,
  rowFilterKey = "all",
} = {}) {
  if (fromDate || toDate) {
    const cacheKey = `${fromDate}|${toDate}|${rowFilterKey}`;
    if (importRowsCache.has(cacheKey)) {
      return importRowsCache.get(cacheKey);
    }
    const { rows } = await loadLineChunkRowsForDateRange(
      { fromDate, toDate },
      undefined,
      { rowFilter }
    );
    importRowsCache.set(cacheKey, rows);
    return rows;
  }
  const weeks = (await listUploadedWeekPeriods()).slice(0, maxWeeks);
  const all = [];
  for (const meta of weeks) {
    const part = await loadWeeklyLineChunkRows(meta.year, meta.monthIndex, meta.weekOfMonth);
    all.push(...part);
  }
  return all;
}

const importRowsCache = new Map();

export function clearSalesImportRowsCache() {
  importRowsCache.clear();
  import("../services/stockReportService")
    .then((m) => m.clearStockReportCache?.())
    .catch(() => {});
  import("../services/movementAnalysisService")
    .then((m) => m.clearMovementAnalysisCache?.())
    .catch(() => {});
}

/** BrandName from SQL import row (preferred for OE vs ordinary on monthly report). */
export function importRowBrandNameField(row) {
  return String(
    row?.brandName ??
      row?.BrandName ??
      row?.brandname ??
      row?.["Brand Name"] ??
      row?.brand ??
      ""
  ).trim();
}

/** Invoice-shaped docs for dashboards/reports that previously read `invoices`. */
export function groupImportRowsAsInvoiceDocs(rows) {
  const grouped = new Map();
  for (const row of rows || []) {
    const voucherNo = String(row.voucherNo || "").trim();
    if (!voucherNo) continue;
    const vt = normalizeVoucherType(row);
    const type = invoiceTypeFromImportRow(row);
    const key = `${vt}::${voucherNo}`;
    if (!grouped.has(key)) {
      grouped.set(key, {
        id: key,
        voucherNo,
        type,
        party: row.customerName || "",
        partyName: row.customerName || "",
        date: row.tranDate || "",
        cashCredit: cashCreditFromVoucherType(vt),
        transactionClass: vt,
        branch: appBranchFromImportRow(row),
        paid: true,
        items: [],
        totals: { net: 0 },
        grandTotal: 0,
        maxBillHeader: 0,
        createdByName: "Sales Import",
        importSource: "sales_import",
      });
    }
    const inv = grouped.get(key);
    const qty = Number(row.qty) || 0;
    const amount = Math.abs(lineAmountOnly(row));
    const billHeader = lineBillAmountHeaderOnly(row);
    if (billHeader > inv.maxBillHeader) inv.maxBillHeader = billHeader;
    const brandName = importRowBrandNameField(row);
    const rawBrand = brandName || String(row.itemGroup || row.itemgroup || "").trim();
    inv.items.push({
      code: row.partNo || "",
      desc: row.itemName || "",
      alias: row.alias || "",
      qty,
      rate: Number(row.unitPrice) || 0,
      productId: row.productId,
      brandName,
      brand: rawBrand,
      mfr: rawBrand,
      /** SQL Amount column — used when qty×rate is zero (common on credit notes). */
      importLineAmount: amount,
    });
    inv.totals.net += amount;
    if (row.tranDate && (!inv.date || String(row.tranDate) > String(inv.date))) {
      inv.date = row.tranDate;
    }
    if (!inv.branch) inv.branch = appBranchFromImportRow(row);
  }
  for (const inv of grouped.values()) {
    const lineSum = (inv.items || []).reduce(
      (s, it) => s + (Number(it.importLineAmount) || 0),
      0
    );
    inv.billAmount = inv.maxBillHeader || 0;
    inv.grandTotal = resolveImportVoucherTotal(lineSum, inv.maxBillHeader || 0);
    inv.totals.net = inv.grandTotal;
    delete inv.maxBillHeader;
  }
  return [...grouped.values()];
}

export async function loadSalesImportInvoiceLikeDocs(opts = {}) {
  const rows = await loadSalesImportRows(opts);
  return groupImportRowsAsInvoiceDocs(rows);
}
